import React, { useEffect, useState } from "react";
import { ClipboardCheck, History, RefreshCw, ExternalLink, Scale, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const RESULT_STYLE = {
  "Atende": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Atende parcialmente": "bg-amber-50 text-amber-700 border-amber-200",
  "Não atende": "bg-rose-50 text-rose-700 border-rose-200",
  "Não aplicável": "bg-slate-100 text-slate-600 border-slate-200",
  "Não avaliado": "bg-slate-50 text-slate-500 border-slate-200",
};
const MON_STYLE = {
  "Monitorado": "text-emerald-700", "Alteração para avaliar": "text-rose-700 font-semibold",
  "Falha de acesso": "text-amber-700", "Aguardando 1ª leitura": "text-slate-500", "Sem link oficial": "text-slate-400",
};

export const ComplianceBadge = ({ value }) => (
  <span className={`inline-block whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-semibold ${RESULT_STYLE[value] || RESULT_STYLE["Não avaliado"]}`}>{value || "Não avaliado"}</span>
);
export const MonitorBadge = ({ value }) => <span className={`whitespace-nowrap text-xs ${MON_STYLE[value] || "text-slate-500"}`}>{value || "-"}</span>;

const EvaluateDialog = ({ row, onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const start = () => { setF({ result: row.compliance && row.compliance !== "Não avaliado" ? row.compliance : "Atende", evidence: row.evidence || "", notes: "", action_plan: row.action_plan || "", action_due: row.action_due || "", next_evaluation: row.next_evaluation || "" }); setOpen(true); };
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/legal-requirements/${row.id}/evaluate`, { ...f, action_due: f.action_due || null, next_evaluation: f.next_evaluation || null });
      toast.success("Avaliação registrada"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const needPlan = ["Atende parcialmente", "Não atende"].includes(f.result);
  return (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Avaliar atendimento" onClick={start} data-testid={`legal-evaluate-btn-${row.id}`}><ClipboardCheck className="h-4 w-4 text-emerald-600" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="legal-evaluate-dialog">
          <DialogHeader><DialogTitle>Avaliar atendimento</DialogTitle><DialogDescription>{row.req_type} {row.number} — ISO 45001 9.1.2</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Resultado</Label>
              <Select value={f.result} onValueChange={(v) => setF({ ...f, result: v })}>
                <SelectTrigger data-testid="legal-eval-result"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(RESULT_STYLE).filter((k) => k !== "Não avaliado").map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Evidência de atendimento</Label><Input value={f.evidence} onChange={(e) => setF({ ...f, evidence: e.target.value })} data-testid="legal-eval-evidence" /></div>
            <div><Label>Observações</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="legal-eval-notes" /></div>
            {needPlan && <div className="grid gap-3 sm:grid-cols-[1fr_150px]">
              <div><Label>Plano de ação *</Label><Textarea rows={2} value={f.action_plan} onChange={(e) => setF({ ...f, action_plan: e.target.value })} data-testid="legal-eval-action" /></div>
              <div><Label>Prazo</Label><Input type="date" value={f.action_due} onChange={(e) => setF({ ...f, action_due: e.target.value })} data-testid="legal-eval-action-due" /></div>
            </div>}
            <div><Label>Próxima avaliação</Label><Input type="date" value={f.next_evaluation} onChange={(e) => setF({ ...f, next_evaluation: e.target.value })} data-testid="legal-eval-next" /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="legal-eval-save">Registrar avaliação</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const KIND = { cadastro: "Cadastro", revisao: "Revisão", avaliacao: "Avaliação", alteracao_legal: "Alteração legal", monitoramento: "Monitoramento", exclusao: "Exclusão" };

const HistoryDialog = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const show = async () => { setOpen(true); const { data } = await api.get(`/legal-requirements/${row.id}/history`); setItems(data); };
  return (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Histórico de revisões" onClick={show} data-testid={`legal-history-btn-${row.id}`}><History className="h-4 w-4 text-slate-500" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="legal-history-dialog">
          <DialogHeader><DialogTitle>Histórico — {row.req_type} {row.number}</DialogTitle><DialogDescription>Informação documentada (ISO 45001 6.1.3 e 7.5)</DialogDescription></DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {items.map((h) => (
              <div key={h.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                <p className="text-xs text-slate-500"><b className="text-[#134074]">{KIND[h.kind] || h.kind}</b> · {new Date(h.at).toLocaleString("pt-BR")} · {h.by}</p>
                <p className="mt-1 text-slate-700">{h.text}</p>
                {h.actions?.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs text-slate-600">{h.actions.map((a) => <li key={a}>{a}</li>)}</ul>}
              </div>
            ))}
            {!items.length && <p className="py-6 text-center text-sm text-slate-400">Sem registros ainda.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const CheckNow = ({ row, onDone }) => {
  const [busy, setBusy] = useState(false);
  const MSG = { baseline: "Primeira leitura salva: a partir de agora as alterações serão detectadas", igual: "Sem alteração na fonte oficial", alterado: "Alteração detectada! Veja o painel de alterações", irrelevante: "A página mudou só no layout — sem alteração na norma", erro: "Não foi possível acessar a fonte oficial", sem_link: "Cadastre o link oficial" };
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/legal-requirements/${row.id}/check-now`, {}, { timeout: 120000 }); toast.info(MSG[data.result] || data.result); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  if (!row.official_url) return null;
  return (
    <>
      <a href={row.official_url} target="_blank" rel="noreferrer" title="Abrir fonte oficial" className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-slate-100" data-testid={`legal-open-${row.id}`}><ExternalLink className="h-4 w-4 text-[#134074]" /></a>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Verificar alterações agora" onClick={run} disabled={busy} data-testid={`legal-check-btn-${row.id}`}><RefreshCw className={`h-4 w-4 text-sky-600 ${busy ? "animate-spin" : ""}`} /></Button>
    </>
  );
};

export const LegalRowActions = ({ row, reload }) => (<><CheckNow row={row} onDone={reload} /><HistoryDialog row={row} /><EvaluateDialog row={row} onDone={reload} /></>);

export function LegalChanges({ refresh, onReviewed }) {
  const { canEdit } = useAuth();
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState({});
  useEffect(() => { api.get("/legal-changes").then(({ data }) => setItems(data)).catch(() => {}); }, [refresh]);
  const pending = items.filter((i) => i.review_status === "Pendente");
  const review = async (id) => {
    try { await api.post(`/legal-changes/${id}/review`, { notes: notes[id] || "Avaliado" }); toast.success("Alteração avaliada"); setItems((x) => x.map((i) => i.id === id ? { ...i, review_status: "Avaliada" } : i)); onReviewed(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!pending.length) return null;
  return (
    <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50/60 p-4" data-testid="legal-changes-panel">
      <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-rose-800"><AlertTriangle className="h-4 w-4" /> {pending.length} alteração(ões) legal(is) aguardando avaliação</p>
      <div className="space-y-3">
        {pending.map((c) => (
          <div key={c.id} className="rounded-lg border border-rose-100 bg-white p-3 text-sm" data-testid={`legal-change-${c.id}`}>
            <p className="font-semibold text-[#0B2545]">{c.req_label} <span className="text-xs font-normal text-slate-500">· {new Date(c.at).toLocaleString("pt-BR")}</span></p>
            <p className="mt-1 text-slate-700"><b>O que mudou:</b> {c.text}</p>
            {c.impact && <p className="mt-1 text-slate-700"><b>Impacto:</b> {c.impact}</p>}
            {c.actions?.length > 0 && <ul className="mt-1 list-disc pl-5 text-slate-700">{c.actions.map((a) => <li key={a}>{a}</li>)}</ul>}
            <p className="mt-1 text-[11px] text-amber-700">Análise gerada por IA — confirme com o texto oficial.</p>
            {canEdit && <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <Input placeholder="Conclusão / providências tomadas" value={notes[c.id] || ""} onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })} data-testid={`legal-change-notes-${c.id}`} />
              <Button onClick={() => review(c.id)} className="bg-[#0B2545] hover:bg-[#134074]" data-testid={`legal-change-review-${c.id}`}><CheckCircle2 className="mr-1.5 h-4 w-4" /> Marcar como avaliada</Button>
            </div>}
          </div>
        ))}
      </div>
    </div>
  );
}

const ISO_ITEMS = [
  ["6.1.3 a) Determinar e ter acesso aos requisitos legais e outros requisitos", "Matriz por esfera (Federal, Estadual, Municipal) e Outros Requisitos, já carregada com a MRL VALBRAC 001, com link direto para a fonte oficial de cada norma."],
  ["6.1.3 b) Determinar como se aplicam à organização e o que precisa ser comunicado", "Cada requisito registra o requisito aplicável à Valbrac, a evidência, o vínculo no SGI, o controle no VIGIA, a periodicidade e o responsável."],
  ["6.1.3 c) Levar em conta ao estabelecer, implementar e melhorar o SGSSO", "Requisitos ligados aos módulos do VIGIA (ASO, EPI, Treinamentos, Licenças, Resíduos, Portais); não atendimento exige plano de ação com prazo."],
  ["6.1.3 — Manter informação documentada e atualizada", "Histórico imutável de cadastro, revisões, avaliações e alterações legais (quem, quando, o quê). Exportação em Excel e PDF."],
  ["Atualização frente a mudanças (6.1.3 e 8.1.3)", "Monitoramento eletrônico diário das fontes oficiais (Planalto, gov.br/MTE, ALESP, LexML, Prefeitura de SP etc.). Alteração detectada gera análise por IA do que mudou e do que fazer, status 'Alteração para avaliar' e e-mail imediato à lista própria."],
  ["9.1.2 Avaliação do atendimento", "Registro de Atende / Atende parcialmente / Não atende / Não aplicável, evidência, data da última e da próxima avaliação, com aviso por e-mail 15 dias antes e em atraso."],
  ["Limites", "Os órgãos públicos não oferecem aviso oficial automático por norma; o VIGIA acompanha as páginas oficiais e a análise da IA é um apoio, que deve ser confirmado pelo responsável legal/técnico."],
];

export function Iso6133Info() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="legal-iso-btn"><Scale className="mr-1.5 h-4 w-4 text-[#134074]" /> Conformidade 6.1.3</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="legal-iso-dialog">
          <DialogHeader><DialogTitle>Como o VIGIA atende a ISO 45001:2018 — 6.1.3</DialogTitle><DialogDescription>Requisitos legais e outros requisitos</DialogDescription></DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto">
            {ISO_ITEMS.map(([t, d], i) => (
              <div key={t} className="rounded-lg border border-slate-200 p-3" data-testid={`legal-iso-item-${i}`}>
                <p className="flex items-center gap-2 text-sm font-semibold text-[#0B2545]"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t}</p>
                <p className="mt-1 text-sm text-slate-600">{d}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
